import React from 'react';
import { MapPin, ExternalLink, Phone, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { usePostHogTracking } from '@/hooks/usePostHogTracking';
import type { StoreEventProps } from '@/lib/posthog';

type GooglePlacesData = {
  placeId: string;
  name: string;
  address: string;
  rating: number;
  reviewCount: number;
  photoUrl?: string;
  phoneNumber?: string;
  website?: string;
  priceLevel?: number;
  openingHours?: string[];
  geometry?: {
    lat: number;
    lng: number;
  };
  types?: string[];
};

type StoreLocationCardProps = {
  storeName: string;
  googlePlaces?: GooglePlacesData;
  basicLocation?: string;
  trackingProps?: StoreEventProps;
};

const getPriceLevelText = (priceLevel?: number): string => {
  if (priceLevel === undefined) return '';
  
  const priceLevels = ['مجاني', 'رخيص', 'متوسط', 'غالي', 'غالي جداً'];
  return priceLevels[priceLevel] || '';
};

export const StoreLocationCard: React.FC<StoreLocationCardProps> = (props) => {
  const { googlePlaces, trackingProps } = props;
  const { trackStore } = usePostHogTracking();
  // If no Google Places data, indicate no physical place and avoid search UI
  if (!googlePlaces?.placeId) {
    return (
      <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
        <CardContent className="p-6 text-center">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <MapPin className="w-6 h-6 text-gray-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 font-arabic">لا يوجد مكان فعلي</h3>
              <p className="text-sm text-gray-600 font-arabic mt-1">
                لا يوجد موقع جغرافي فعلي متاح لهذا المتجر
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If we have Google Places data
  if (googlePlaces) {
    const { geometry, address, rating, reviewCount, phoneNumber, website, priceLevel, openingHours } = googlePlaces;
    
    const googleMapsUrl = geometry 
      ? `https://www.google.com/maps/search/?api=1&query=${geometry.lat},${geometry.lng}&query_place_id=${googlePlaces.placeId}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

    return (
      <Card className="border border-green-200 bg-green-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-green-800 font-arabic">
            <MapPin className="w-5 h-5" />
            الموقع الجغرافي
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Address */}
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-900 font-arabic">{address}</p>
              {rating > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-yellow-500">★</span>
                  <span className="text-sm text-gray-600 font-arabic">
                    {rating.toFixed(1)} ({reviewCount} تقييم)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Info */}
          <div className="space-y-2">
            {phoneNumber && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" />
                <span className="font-arabic" dir="ltr">{phoneNumber}</span>
              </div>
            )}
            
            {website && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Globe className="w-4 h-4" />
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackStore.contactClicked({
                      platform: 'website',
                      surface: 'location_card',
                      ...trackingProps,
                    })
                  }
                  className="text-blue-600 hover:underline font-arabic"
                >
                  زيارة الموقع الإلكتروني
                </a>
              </div>
            )}

            {priceLevel !== undefined && (
              <div className="text-sm text-gray-600 font-arabic">
                <span className="font-medium">مستوى الأسعار: </span>
                {getPriceLevelText(priceLevel)}
              </div>
            )}
          </div>

          {/* Opening Hours */}
          {openingHours && openingHours.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 font-arabic mb-2">ساعات العمل</h4>
              <div className="space-y-1">
                {openingHours.slice(0, 3).map((hours, index) => (
                  <p key={index} className="text-xs text-gray-600 font-arabic">
                    {hours}
                  </p>
                ))}
                {openingHours.length > 3 && (
                  <p className="text-xs text-gray-500 font-arabic">
                    و {openingHours.length - 3} أيام أخرى...
                  </p>
                )}
              </div>
            </div>
          )}

          {/* View on Google Maps Button */}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              trackStore.contactClicked({
                platform: 'google_maps',
                surface: 'location_card',
                ...trackingProps,
              });
              window.open(googleMapsUrl, '_blank');
            }}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            <span className="font-arabic">عرض في خرائط جوجل</span>
          </Button>

          {/* Google Places Attribution */}
          <p className="text-xs text-gray-500 font-arabic text-center">
            البيانات مدعومة من Google Places
          </p>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default StoreLocationCard;
